import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import WorldEngine, { detectQuality } from "@/components/WorldEngine";
import ShareDialog from "@/components/ShareDialog";
import { publishShareCard } from "@/lib/studioCanvas";
import {
  ArrowLeft, Target, Trophy, Zap, Sparkles, Share2, RotateCcw,
  ShoppingBag, Wand2, AlertTriangle, X, HelpCircle, Hand, ZoomIn, MousePointerClick, Flag, Radio,
} from "lucide-react";

const HELP_KEY = "aether_world_help_seen";
const HELP_STEPS = [
  { icon: Hand, label: "Drag / swipe to orbit the sector and view it from any angle." },
  { icon: ZoomIn, label: "Pinch or scroll to zoom; two-finger drag (or right-drag) to pan and roam freely." },
  { icon: MousePointerClick, label: "Tap the glowing shards to collect them toward the objective." },
  { icon: Zap, label: "Tap the central structure to send a resonance pulse." },
  { icon: Radio, label: "Watch the Transmission Log — the sector's story unfolds as you collect." },
  { icon: Flag, label: "Fill the objective bar to stabilize the sector and earn its sigil." },
];

function hasWebGL() {
  try {
    const c = document.createElement("canvas");
    return !!(window.WebGLRenderingContext && (c.getContext("webgl") || c.getContext("experimental-webgl")));
  } catch { return false; }
}

export default function WorldView() {
  const { envId } = useParams();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState(null);
  const [collected, setCollected] = useState(0);
  const [reward, setReward] = useState(null);
  const [share, setShare] = useState({ open: false, dataUrl: null, url: null });
  const [runKey, setRunKey] = useState(0);
  const [pulses, setPulses] = useState(0);
  const got = useRef(new Set());
  const startAt = useRef(Date.now());
  const webgl = useRef(hasWebGL());
  const completing = useRef(false);
  const [help, setHelp] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem(HELP_KEY)) setHelp(true);
  }, []);
  const dismissHelp = () => { localStorage.setItem(HELP_KEY, "1"); setHelp(false); };
  const quality = useRef(detectQuality());

  useEffect(() => {
    got.current = new Set(); setCollected(0); setReward(null); completing.current = false;
    startAt.current = Date.now();
    api.get(`/sim/world/${envId}`).then((r) => setCfg(r.data)).catch((e) => {
      if (e?.response?.status === 403) { toast.error("Sector locked — upgrade to enter"); navigate("/pricing"); }
      else setError("Sector not found");
    });
  }, [envId, navigate, runKey]);

  const finish = useCallback(async () => {
    if (completing.current) return;
    completing.current = true;
    const seconds = Math.round((Date.now() - startAt.current) / 1000);
    try {
      const r = await api.post(`/sim/world/${envId}/complete`, { shards: got.current.size, seconds });
      setReward({ ...r.data, seconds });
    } catch { setReward({ sigil: cfg?.world?.sigil || "Explorer", message: "Sector stabilized.", seconds }); }
  }, [envId, cfg]);

  const onCollect = useCallback((id) => {
    if (got.current.has(id)) return;
    got.current.add(id);
    const n = got.current.size;
    setCollected(n);
    if (cfg && n >= cfg.world.shards) finish();
  }, [cfg, finish]);

  const onPulse = useCallback(() => setPulses((p) => p + 1), []);

  const replay = () => { setRunKey((k) => k + 1); };

  const shareWorld = async () => {
    const canvas = document.querySelector('[data-testid="world-view"] canvas');
    if (!canvas) { toast.error("Render not ready"); return; }
    try {
      const dataUrl = canvas.toDataURL("image/png");
      const { url, nativeShared } = await publishShareCard(dataUrl, `Exploring ${cfg?.name} on AETHER`);
      if (!nativeShared) setShare({ open: true, dataUrl, url });
      else toast.success("Shared!");
    } catch { toast.error("Share failed"); }
  };

  if (error) return (
    <div className="min-h-screen bg-app flex items-center justify-center">
      <div className="text-center"><AlertTriangle className="w-8 h-8 text-amber mx-auto mb-3" />
        <p className="text-slate-400">{error}</p>
        <Link to="/simulation" className="btn-hud inline-flex mt-5 border border-cyan/40 text-cyan px-5 py-2.5 text-sm hover:bg-cyan hover:text-app">Back to Directory</Link></div>
    </div>
  );

  if (!cfg) return (
    <div className="min-h-screen bg-app flex items-center justify-center">
      <div className="font-mono text-xs tracking-[0.3em] text-cyan/70 animate-pulse-glow">MATERIALISING SECTOR...</div>
    </div>
  );

  const accent = cfg.accent;
  const total = cfg.world.shards;
  const pct = Math.round((collected / total) * 100);

  // Interactive storytelling — beats reveal as shards are collected
  const story = cfg.world.story || [];
  const storyIdx = reward
    ? story.length - 1
    : Math.min(story.length - 1, Math.floor((collected / total) * story.length));
  const currentBeat = story[Math.max(0, storyIdx)];

  return (
    <div className="fixed inset-0 z-50 bg-app overflow-hidden" data-testid="world-view">
      {/* 3D layer */}
      <div className="absolute inset-0">
        {webgl.current ? (
          <WorldEngine key={runKey} world={cfg.world} accent={accent} quality={quality.current} onCollect={onCollect} onPulse={onPulse} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center"><AlertTriangle className="w-7 h-7 text-amber mx-auto mb-2" />
              <p className="font-mono text-[10px] tracking-widest text-amber/80">WEBGL UNAVAILABLE ON THIS DEVICE</p></div>
          </div>
        )}
      </div>

      <div className="pointer-events-none absolute inset-0 hud-scanlines opacity-30" />

      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 z-30 flex items-center justify-between p-5">
        <button onClick={() => navigate("/simulation")} data-testid="world-exit"
          className="btn-hud flex items-center gap-2 glass border border-cyan/30 text-cyan px-4 py-2 text-xs hover:bg-cyan hover:text-app">
          <ArrowLeft className="w-3.5 h-3.5" /> Directory
        </button>
        <div className="text-center">
          <div className="font-mono text-[10px] tracking-[0.4em]" style={{ color: accent }}>{cfg.biome?.toUpperCase()}</div>
          <h1 className="font-head text-xl sm:text-2xl font-bold uppercase" style={{ color: accent }}>{cfg.name}</h1>
        </div>
        <button onClick={shareWorld} data-testid="world-share"
          className="btn-hud flex items-center gap-1.5 glass border border-amber/40 text-amber px-3 py-2 text-xs hover:bg-amber hover:text-app">
          <Share2 className="w-3.5 h-3.5" /> Share
        </button>
      </div>

      {/* Objective + progress */}
      <div className="absolute top-24 left-5 z-30 max-w-xs glass-strong clip-chamfer brackets p-4" data-testid="world-objective">
        <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-cyan/70 mb-2"><Target className="w-3.5 h-3.5" /> OBJECTIVE</div>
        <p className="text-sm text-slate-200 leading-snug">{cfg.world.game_objective}</p>
        <div className="mt-3">
          <div className="flex justify-between font-mono text-[10px] tracking-widest mb-1">
            <span className="text-slate-400">SHARDS</span>
            <span style={{ color: accent }} data-testid="shard-count">{collected} / {total}</span>
          </div>
          <div className="h-1.5 bg-white/10 overflow-hidden">
            <div className="h-full transition-all duration-300" style={{ width: `${pct}%`, background: accent, boxShadow: `0 0 10px ${accent}` }} />
          </div>
        </div>
        <div className="flex items-center gap-1.5 mt-3 font-mono text-[9px] tracking-wide text-slate-500">
          <Sparkles className="w-3 h-3" /> {cfg.world.tip}
        </div>
      </div>

      {/* Transmission Log — interactive storytelling */}
      {currentBeat && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-30 w-[min(92vw,30rem)] pointer-events-none" data-testid="world-story">
          <div className="glass-strong clip-chamfer brackets px-5 py-3 text-center">
            <div className="flex items-center justify-center gap-2 font-mono text-[9px] tracking-[0.35em] text-cyan/70 mb-1">
              <Radio className="w-3 h-3" /> TRANSMISSION LOG · {Math.max(1, storyIdx + 1)}/{story.length}
            </div>
            <AnimatePresence mode="wait">
              <motion.p
                key={storyIdx}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.4 }}
                className="text-sm text-slate-200 italic leading-snug"
                data-testid="story-beat"
              >
                “{currentBeat}”
              </motion.p>
            </AnimatePresence>
          </div>
        </div>
      )}

      {/* Pulse counter (flavor) */}
      <div className="absolute bottom-5 left-5 z-30 flex items-center gap-2 glass border border-white/10 px-3 py-2">
        <Zap className="w-3.5 h-3.5 text-amber" />
        <span className="font-mono text-[10px] tracking-widest text-slate-300">RESONANCE {pulses}</span>
      </div>

      {/* Completion reward */}
      {reward && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-app/85 backdrop-blur-sm p-5" data-testid="world-reward">
          <div className="relative w-full max-w-md glass-strong clip-chamfer brackets p-8 text-center">
            <button onClick={() => setReward(null)} className="absolute top-3 right-3 text-slate-400 hover:text-cyan"><X className="w-5 h-5" /></button>
            <Trophy className="w-12 h-12 mx-auto mb-4" style={{ color: accent }} />
            <div className="font-mono text-[10px] tracking-[0.4em] text-cyan/70">SECTOR STABILIZED</div>
            <h2 className="font-head text-3xl font-bold uppercase mt-2" style={{ color: accent }}>{reward.sigil} Sigil</h2>
            <p className="text-sm text-slate-400 mt-3">{reward.message}</p>
            <div className="flex justify-center gap-6 mt-5 font-mono text-[10px] tracking-widest text-slate-400">
              <div><div className="text-2xl font-head text-slate-100">{collected}</div>SHARDS</div>
              <div><div className="text-2xl font-head text-slate-100">{reward.seconds}s</div>TIME</div>
              {reward.score != null && <div><div className="text-2xl font-head" style={{ color: accent }}>{reward.score}</div>SCORE</div>}
            </div>
            <div className="grid grid-cols-2 gap-2 mt-7">
              <Link to="/studio" data-testid="reward-studio" className="btn-hud flex items-center justify-center gap-2 border border-cyan text-cyan py-2.5 text-xs box-glow-cyan hover:bg-cyan hover:text-app"><Wand2 className="w-3.5 h-3.5" /> Design Apparel</Link>
              <Link to="/shop" data-testid="reward-shop" className="btn-hud flex items-center justify-center gap-2 border border-amber/40 text-amber py-2.5 text-xs hover:bg-amber hover:text-app"><ShoppingBag className="w-3.5 h-3.5" /> Shop Sector</Link>
            </div>
            <div className="flex justify-center gap-4 mt-4">
              <button onClick={replay} data-testid="reward-replay" className="flex items-center gap-1.5 font-mono text-[10px] tracking-widest text-slate-400 hover:text-cyan"><RotateCcw className="w-3.5 h-3.5" /> REPLAY</button>
              <button onClick={() => navigate("/simulation")} className="flex items-center gap-1.5 font-mono text-[10px] tracking-widest text-slate-400 hover:text-cyan"><ArrowLeft className="w-3.5 h-3.5" /> EXIT</button>
            </div>
          </div>
        </div>
      )}

      {/* Help button */}
      <button onClick={() => setHelp(true)} data-testid="world-help-btn"
        className="absolute bottom-5 right-5 z-30 w-11 h-11 flex items-center justify-center glass border border-cyan/30 text-cyan hover:bg-cyan hover:text-app transition-colors rounded-full">
        <HelpCircle className="w-5 h-5" />
      </button>

      {/* Onboarding help panel (dismissible) */}
      {help && (
        <div className="absolute inset-0 z-[55] flex items-center justify-center bg-app/80 backdrop-blur-sm p-5" data-testid="world-help-panel" onClick={dismissHelp}>
          <div className="relative w-full max-w-sm glass-strong clip-chamfer brackets p-6" onClick={(e) => e.stopPropagation()}>
            <button onClick={dismissHelp} data-testid="world-help-close" className="absolute top-3 right-3 text-slate-400 hover:text-cyan"><X className="w-5 h-5" /></button>
            <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] text-cyan/70 mb-1"><HelpCircle className="w-3.5 h-3.5" /> NAVIGATION BRIEFING</div>
            <h3 className="font-head text-xl font-bold uppercase mb-4" style={{ color: accent }}>How to explore {cfg.name}</h3>
            <div className="space-y-3">
              {HELP_STEPS.map((s, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="shrink-0 w-8 h-8 flex items-center justify-center border border-cyan/30 text-cyan"><s.icon className="w-4 h-4" /></div>
                  <p className="text-sm text-slate-300 leading-snug pt-1.5">{s.label}</p>
                </div>
              ))}
            </div>
            <button onClick={dismissHelp} data-testid="world-help-begin"
              className="btn-hud w-full mt-6 flex items-center justify-center gap-2 border border-cyan text-cyan py-2.5 text-sm box-glow-cyan hover:bg-cyan hover:text-app">
              Begin Exploration
            </button>
          </div>
        </div>
      )}

      <ShareDialog open={share.open} onClose={() => setShare({ open: false, dataUrl: null, url: null })}
        shareUrl={share.url} dataUrl={share.dataUrl} caption={`${cfg.name} — AETHER`} />
    </div>
  );
}
